import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { api, errorText } from "../lib/api";
import { Overview, Submissions, Messages, Subscribers, Orders, ArticlesAdmin } from "../components/AdminTabs";
import { IssuesAdmin, ReadersAdmin } from "../components/AdminIssues";

const TABS = [["overview", "Overview", Overview], ["submissions", "Submissions", Submissions], ["issues", "Issues & PDFs", IssuesAdmin], ["articles", "Articles", ArticlesAdmin], ["readers", "Readers", ReadersAdmin], ["messages", "Messages", Messages], ["subscribers", "Newsletter", Subscribers], ["orders", "Orders", Orders]];

function Login({ onIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("bpj_token", data.token);
      if (data.role !== "admin") { setErr("This account doesn't have editor access."); setBusy(false); return; }
      onIn(data);
    } catch (e2) { setErr(errorText(e2)); }
    setBusy(false);
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="admin-login-page">
      <div className="hidden lg:block relative bg-[#0D1B3E] grain">
        <img src="/img/product.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute bottom-12 left-12 text-white"><p className="f-serif text-5xl leading-tight">Editors' desk.</p></div>
      </div>
      <div className="flex flex-col justify-center px-6 sm:px-16 py-16">
        <Link to="/" className="mb-16" data-testid="admin-home-link"><Logo /></Link>
        <h1 className="f-serif text-4xl sm:text-5xl">Sign in</h1>
        <p className="text-[var(--muted)] mt-2">Private area for the BPJ editorial team.</p>
        <form onSubmit={submit} className="mt-10 space-y-8 max-w-md" data-testid="admin-login-form">
          <label className="block"><span className="eyebrow">Email</span><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="field" data-testid="admin-email-input" /></label>
          <label className="block"><span className="eyebrow">Password</span><input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="field" data-testid="admin-password-input" /></label>
          {err && <p className="text-sm text-[var(--red)]" data-testid="admin-login-error">{err}</p>}
          <button disabled={busy} className="btn-ink h-12 px-10 font-semibold w-full" data-testid="admin-login-submit">{busy ? "Signing in…" : "Sign in"}</button>
        </form>
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("overview");
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data?.role === "admin" ? r.data : null)).catch(() => setUser(null)).finally(() => setChecking(false));
  }, []);
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("bpj_token");
    setUser(null);
    toast.success("Signed out");
  };
  if (checking) return <div className="min-h-screen" />;
  if (!user) return <Login onIn={setUser} />;
  const Active = TABS.find((t) => t[0] === tab)[2];
  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="flex items-center justify-between px-5 lg:px-10 h-16 border-b rule">
        <Link to="/"><Logo /></Link>
        <div className="flex items-center gap-4 text-sm"><span className="hidden sm:inline text-[var(--muted)]">{user.email}</span>
          <button onClick={logout} className="flex items-center gap-2 link-u" data-testid="admin-logout-btn"><LogOut size={16} /> Sign out</button></div>
      </header>
      <nav className="flex gap-6 px-5 lg:px-10 border-b rule overflow-x-auto">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`py-4 whitespace-nowrap text-sm border-b-2 transition-colors ${tab === k ? "border-[var(--red)] font-semibold" : "border-transparent text-[var(--muted)] hover:text-[var(--ink)]"}`}>{l}</button>)}
      </nav>
      <div className="px-5 lg:px-10 py-10"><Active /></div>
    </div>
  );
}
